import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { ModalNext, ModalHeader, ModalBody, ModalFooter } from '@jutro/components';
import _ from 'lodash';

import { Button } from '@jutro/legacy/components';

import metadata from './PeriodVersionDiff.metadata.json5'
import messages from './PeriodVersionDiff.messages'
import styles from './PeriodVersionDiff.module.scss'

function PeriodVersionDiffPopup(props) {
  const { data, isOpen, onSaveAction, onReject, onResolve } = props;
  const translator = useTranslator();
  const [didUpdate, setDidUpdate] = useState(false);

  const handleClose = useCallback(() => {
  data.version1 = undefined;
  data.version1Index = undefined;
  data.version2 = undefined;
  data.version2Index = undefined;
    onReject();
  }, [onReject]);

  const handleSave = useCallback(() => {
    if (onSaveAction && !onSaveAction(data)) {
      return;
    }
    onResolve(data);
  }, [data, onResolve, onSaveAction]);

  if (!isOpen) {
    return null;
  }

  const mapAvailableValues = (values) => {
  // Use the original index as the option code so duplicate string values
  // are treated as distinct options. This allows selecting the same
  // label in both dropdowns when duplicates exist in versionList1.
  return values.map((type, idx) => {
    const value = typeof type === 'string' ? type : type.name;
    return {
      // code is the index in the original array (stringified to be safe)
      code: String(idx),
      name: translator({
        id: value,
        defaultMessage: value,
      }),
      // keep original value for convenience
      _value: value,
    };
  });
  };

  const transactionSelectVersion1ValueChange = (newValue) => {
  // newValue will be the stringified index of the selected item in data.versionList1
  if (newValue === undefined || newValue === null) {
    data.version1 = undefined;
    data.version1Index = undefined;
    data.version2 = undefined;
    data.version2Index = undefined;
    // clear any explicit versionList2 stored on the model
    data.versionList2 = [];
  } else {
    const selectedIdx = Number(newValue);
    // guard: if index is invalid, reset
    if (Number.isNaN(selectedIdx) || !Array.isArray(data.versionList1) || selectedIdx < 0 || selectedIdx >= data.versionList1.length) {
      data.version1 = undefined;
      data.version1Index = undefined;
      data.version2 = undefined;
      data.version2Index = undefined;
    } else {
      // Set version1 to the actual string value; also keep the selected index
      data.version1 = data.versionList1[selectedIdx];
      data.version1Index = selectedIdx;
    }
  }
  setDidUpdate((s) => !s);
};

  const transaction2ValueChange = (newValue) => {
    // newValue will be the stringified index (from the original versionList1)
    if (newValue === undefined || newValue === null) {
      data.version2 = undefined;
      data.version2Index = undefined;
    } else {
      const selectedIdx = Number(newValue);
      if (Number.isNaN(selectedIdx) || !Array.isArray(data.versionList1) || selectedIdx < 0 || selectedIdx >= data.versionList1.length) {
        data.version2 = undefined;
        data.version2Index = undefined;
      } else {
        data.version2 = data.versionList1[selectedIdx];
        data.version2Index = selectedIdx;
      }
    }
    setDidUpdate((s) => !s);
  };

  const overrideProps = {
    transactionSelectVersion1: {
      // Provide option codes as indexes so identical labels stay distinct
      availableValues: Array.isArray(data.versionList1) ? mapAvailableValues(data.versionList1) : [],
      onValueChange: (value1) => transactionSelectVersion1ValueChange(value1),
      // Value for this dropdown is the selected index (string) or null
      value: data.version1Index !== undefined && data.version1Index !== null ? String(data.version1Index) : null,
    },
    transactionSelectVersion2: {
      // Build version2 options from the original list but exclude the selected version1 index
      availableValues: Array.isArray(data.versionList2)
        ? mapAvailableValues(data.versionList2)
        : [],
      onValueChange: (value1) => transaction2ValueChange(value1),
      value: data.version2Index !== undefined && data.version2Index !== null ? String(data.version2Index) : null,
    },
  };

  return (
    <ModalNext
      className={styles["ml-modal-dialog"]}
      isOpen={isOpen}
      onRequestClose={handleClose}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={false}
    >
      <ModalHeader
        title={messages.versionDiffTitle}
        onClose={handleClose}
        contentLayout={{
          component: "div",
          componentProps: {
            className: styles["modal-header"],
          },
        }}
        titleLayout={{
          component: "flex",
          componentProps: {
            direction: "column",
            className: styles["modal-title"],
          },
        }}
      />
      <ModalBody
        contentLayout={{
          component: "div",
          componentProps: {
            className: styles["modal-body"],
          },
        }}
      >
        <ViewModelForm
          uiProps={metadata.pageContent}
          model={data}
          overrideProps={overrideProps}
        />
      </ModalBody>
      <ModalFooter
        contentLayout={{
          component: "flex",
          componentProps: {
            className: styles["modal-footer"],
          },
        }}
      >
        <Button
          id="actionButton"
          onClick={handleSave}
          type="filled"
          disabled={!data.version1 || !data.version2 || (data.version1Index === data.version2Index)}
        >
          {translator(messages.Compare)}
        </Button>
        <Button id="cancelActionButton" onClick={handleClose} type="outlined">
          {translator(messages.Cancel)}
        </Button>
      </ModalFooter>
    </ModalNext>
  );
}

PeriodVersionDiffPopup.propTypes = {
    props: PropTypes.shape({
        data: PropTypes.shape([]),
        isOpen: PropTypes.bool,
        onSaveAction: PropTypes.func,
        onResolve: PropTypes.func,
        onReject: PropTypes.func
    })
};
PeriodVersionDiffPopup.defaultProps = {
    props: {
        data: [],
        isOpen: false,
        onSaveAction: _.noop,
        onResolve: _.noop,
        onReject: _.noop
    }
};
export default PeriodVersionDiffPopup;